import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import {
  COPY_ROOM_OVERLAY,
  DELETE_ROOM_OVERLAY,
  EDIT_ROOM_OVERLAY
} from "shared/context/overlay/types"
import { Logger } from "shared/lib/Logger"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ProjectPermissions } from "shared/types/__generated__/graphql"

import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { useDeleteRoom } from "entities/rooms/api/useDeleteRoom"
import { useDuplicateRoom } from "entities/rooms/api/useDuplicateRoom"
import { useRoomFragment } from "entities/rooms/model/useRoomFragment"
import { RoomFormTabType } from "features/room-management/types/RoomFormTabType"

interface UseRoomActionHandlersArgs {
  projectID: string
  roomID: string
  permissions: ProjectPermissions
  onAction?: () => void
}

export type FilteredRoomActionHandlers = {
  onEdit: (() => void) | undefined
  onDuplicate: (() => void) | undefined
  onCopy: (() => void) | undefined
  onAssignAttachments: (() => void) | undefined
  onAddClarificationRequest: (() => void) | undefined
  onDelete: (() => void) | undefined
}

export function useRoomActionHandlers({
  projectID,
  roomID,
  permissions,
  onAction
}: UseRoomActionHandlersArgs): FilteredRoomActionHandlers {
  const { activeProjectID } = useNeoletterUserProfile()
  const room = useRoomFragment(roomID)
  const [deleteRoom] = useDeleteRoom()
  const [duplicateRoom] = useDuplicateRoom()
  const { openOverlay: openDeleteRoomOverlay } = useOverlay(DELETE_ROOM_OVERLAY)
  const { openOverlay: editRoomOverlay } = useOverlay(EDIT_ROOM_OVERLAY)
  const { openOverlay: openCopyRoomOverlay } = useOverlay(COPY_ROOM_OVERLAY)

  const handleEditRoom = useEvent(() => {
    editRoomOverlay({ projectID, roomID })
    onAction?.()
  })

  const handleAssignAttachments = useEvent(() => {
    editRoomOverlay({
      projectID,
      roomID,
      defaultTab: RoomFormTabType.ATTACHMENTS
    })
    onAction?.()
  })

  const handleAddClarificationRequest = useEvent(() => {
    editRoomOverlay({
      projectID,
      roomID,
      defaultTab: RoomFormTabType.CLARIFICATION_REQUEST
    })
    onAction?.()
  })

  const handleDuplicateRoom = useEvent(() => {
    if (room) {
      // duplicateRoom already toasts and rolls back the cache; catch so the
      // re-thrown reason does not surface as an unhandled rejection.
      duplicateRoom({ projectID, roomID, position: room.position + 1 }).catch(
        e => Logger.error(e, "Failed to duplicate room", { projectID, roomID })
      )
      onAction?.()
    }
  })

  const handleCopyRoom = useEvent(() => {
    openCopyRoomOverlay({
      roomID,
      projectID: activeProjectID || projectID
    })
    onAction?.()
  })

  const handleDeleteRoom = useEvent(() => {
    deleteRoom({ id: roomID }, projectID)
    onAction?.()
  })

  const handleOpenDeleteRoomOverlay = useEvent(() => {
    if (room) {
      openDeleteRoomOverlay({ projectID, roomsToDelete: [room] })
      onAction?.()
    }
  })

  const hasChildren = !room?.children || room.children.length > 0
  const canEdit = permissions[ProjectPermissionType.EDIT]
  const deleteHandler = hasChildren
    ? handleOpenDeleteRoomOverlay
    : handleDeleteRoom

  return {
    onEdit: canEdit ? handleEditRoom : undefined,
    onDuplicate: canEdit ? handleDuplicateRoom : undefined,
    onCopy: canEdit ? handleCopyRoom : undefined,
    onAssignAttachments: canEdit ? handleAssignAttachments : undefined,
    onAddClarificationRequest: canEdit
      ? handleAddClarificationRequest
      : undefined,
    onDelete: canEdit ? deleteHandler : undefined
  }
}
